import { computed, reactive, readonly } from 'vue'
import { DomStorage, MetadataKey } from '@/enums'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import appStore from '@/stores/app.store'
import accountStore from '@/stores/account.store'
import GoogleTagManagerService from '@/services/google-tag-manager.service'
import unitStore from '@/stores/unit.store'

interface accountStoreStates {
  token: string | null
}

const state = reactive({
  token: sessionStorage.getItem(DomStorage.AUTH_TOKEN),
} as accountStoreStates)

const checkRouteAuth = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  /**
   * Clear PII data from calendly links
   * @see https://careerpartner.atlassian.net/browse/MPAD-1157
   */
  if (to.query && to.query['assigned_to']) {
    return next({ query: {} })
  }

  if (to.path === '/maintenance') {
    return next()
  }

  // Check Auth Redirect and tokens
  if (to.hash && !['#motivation'].includes(to.hash)) {
    // is redirect from Auth0

    const hash = to.hash.replace('#access_token', '?access_token')
    const urlParams = new URLSearchParams(hash)

    const accessToken = urlParams.get('access_token') || ''

    // load last url path | MPAD-643
    const lastUrl = sessionStorage.getItem(DomStorage.ORIGIN_URL_PATH)

    // Use Auth0 token directly for both B2B and B2C (OCPT-640)
    state.token = accessToken
    sessionStorage.setItem(DomStorage.AUTH_TOKEN, accessToken)
    return next(lastUrl ?? to.path)
  }

  //## Route call to route without authorization needed (like privacy policy) -> No Token needed
  if (to.meta.allowUnauthorized) {
    appStore.setDisableNavbar(true)
    appStore.toggleInitialLoading()
    return next()
  }

  /** validation of token through apiService **/
  if (!state.token && !appStore.state.apiError) {
    // load last url path | MPAD-643
    sessionStorage.setItem(DomStorage.ORIGIN_URL_PATH, to.fullPath)
    console.log('[REDIRECT] set lastUrl', to.fullPath)
    sessionStorage.setItem(
      DomStorage.ORIGIN_URL_QUERIES,
      JSON.stringify({ time: new Date(), queries: to.query }),
    )
    await appStore.redirectToLogin()
    return false
  }

  // Don't redirect to onboarding if user is viewing document preview
  if (to.name === 'DocumentsPreview') {
    return next()
  }

  if (
    !appStore.state.initialLoading &&
    !onboardingDone.value &&
    to.meta.onboarding &&
    to.name !== to.meta.onboarding
  ) {
    return next({ name: 'ds.home' })
  }

  // disable navbar if in route meta
  appStore.setDisableNavbar(!!to.meta.noNav)

  return next()
}

const onboardingDone = computed(() => {
  const onboardingIsDone = accountStore.getMetadataValue(
    MetadataKey.ONBOARDING_DONE,
  )
  const interviewIsDone = unitStore.state?.unit?.participateAdvisorInterviewOn

  return onboardingIsDone || interviewIsDone
})

const logoutUser = () => {
  GoogleTagManagerService.trackEvent('LogoutClick', 'logout')
  state.token = null
  sessionStorage.removeItem(DomStorage.AUTH_TOKEN)
  localStorage.clear()
  const logoutUrl = appStore.state.isB2b
    ? `${import.meta.env.VITE_AUTH0_LOGOUT_URL}${appStore.state.baseUrl}`
    : `${import.meta.env.VITE_AUTH0_B2C_LOGOUT_URL}${appStore.state.baseUrl}`
  console.log('logoutUser with redirect', logoutUrl)
  window.location.replace(logoutUrl)
}

// Export complete store at once
export default {
  state: readonly(state),
  checkRouteAuth,
  logoutUser,
  onboardingDone,
}
